// Touch gesture controller for the game map canvas.
//
// Two-finger pan and pinch-to-zoom. Single-finger tap and drag are
// routed to the game for interaction (placement, movement, targeting).
// Calls preventDefault on touch events to suppress browser zoom/scroll
// on the canvas while allowing other elements to scroll normally.

const TAP_THRESHOLD_PX = 10;
const TAP_THRESHOLD_MS = 300;

export class TouchController {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {object} callbacks
   * @param {(dx: number, dy: number) => void} callbacks.onPan
   * @param {(scaleDelta: number, centerX: number, centerY: number) => void} callbacks.onPinchZoom
   * @param {(clientX: number, clientY: number) => void} callbacks.onTap
   * @param {(clientX: number, clientY: number) => void} callbacks.onDragStart
   * @param {(clientX: number, clientY: number) => void} callbacks.onDragMove
   * @param {(clientX: number, clientY: number) => void} callbacks.onDragEnd
   */
  constructor(canvas, callbacks) {
    this._canvas = canvas;
    this._callbacks = callbacks;

    // Single-finger tracking
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touchStartTime = 0;
    this._lastTouchX = 0;
    this._lastTouchY = 0;
    this._isSingleFingerDrag = false;

    // Two-finger tracking
    this._lastPanCenterX = 0;
    this._lastPanCenterY = 0;
    this._initialPinchDistance = 0;
    this._isTwoFingerGesture = false;

    // Bind handlers
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);

    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this._onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', this._onTouchEnd, { passive: false });

    // Suppress long-press context menu on mobile
    this._onContextMenu = (e) => e.preventDefault();
    canvas.addEventListener('contextmenu', this._onContextMenu);
  }

  destroy() {
    this._canvas.removeEventListener('touchstart', this._onTouchStart);
    this._canvas.removeEventListener('touchmove', this._onTouchMove);
    this._canvas.removeEventListener('touchend', this._onTouchEnd);
    this._canvas.removeEventListener('touchcancel', this._onTouchEnd);
    this._canvas.removeEventListener('contextmenu', this._onContextMenu);
  }

  /** @param {TouchEvent} e */
  _onTouchStart(e) {
    e.preventDefault();

    if (e.touches.length === 2) {
      // Start two-finger gesture (pan + pinch)
      this._isTwoFingerGesture = true;
      this._isSingleFingerDrag = false;
      this._initialPinchDistance = this._getTouchDistance(e.touches[0], e.touches[1]);
      this._lastPanCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      this._lastPanCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      this._touchStartX = touch.clientX;
      this._touchStartY = touch.clientY;
      this._lastTouchX = touch.clientX;
      this._lastTouchY = touch.clientY;
      this._touchStartTime = Date.now();
      this._isSingleFingerDrag = false;
    }
  }

  /** @param {TouchEvent} e */
  _onTouchMove(e) {
    e.preventDefault();

    if (this._isTwoFingerGesture && e.touches.length === 2) {
      // Two-finger pan
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const dx = centerX - this._lastPanCenterX;
      const dy = centerY - this._lastPanCenterY;
      this._callbacks.onPan(dx, dy);
      this._lastPanCenterX = centerX;
      this._lastPanCenterY = centerY;

      // Pinch zoom
      const newDistance = this._getTouchDistance(e.touches[0], e.touches[1]);
      if (this._initialPinchDistance > 0) {
        const scaleDelta = newDistance / this._initialPinchDistance;
        this._callbacks.onPinchZoom(scaleDelta, centerX, centerY);
        this._initialPinchDistance = newDistance;
      }
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];

      // Check if we've moved enough to start dragging
      const totalDx = touch.clientX - this._touchStartX;
      const totalDy = touch.clientY - this._touchStartY;
      if (!this._isSingleFingerDrag && (Math.abs(totalDx) > TAP_THRESHOLD_PX || Math.abs(totalDy) > TAP_THRESHOLD_PX)) {
        this._isSingleFingerDrag = true;
        this._callbacks.onDragStart(this._touchStartX, this._touchStartY);
      }

      if (this._isSingleFingerDrag) {
        this._callbacks.onDragMove(touch.clientX, touch.clientY);
      }

      this._lastTouchX = touch.clientX;
      this._lastTouchY = touch.clientY;
    }
  }

  /** @param {TouchEvent} e */
  _onTouchEnd(e) {
    if (this._isTwoFingerGesture) {
      if (e.touches.length === 0) {
        this._isTwoFingerGesture = false;
        this._initialPinchDistance = 0;
      }
      return;
    }

    if (e.touches.length === 0) {
      if (this._isSingleFingerDrag) {
        this._callbacks.onDragEnd(this._lastTouchX, this._lastTouchY);
      } else {
        // Detect tap: short duration, minimal movement
        const elapsed = Date.now() - this._touchStartTime;
        if (elapsed < TAP_THRESHOLD_MS) {
          this._callbacks.onTap(this._touchStartX, this._touchStartY);
        }
      }
      this._isSingleFingerDrag = false;
    }
  }

  /**
   * @param {Touch} t1
   * @param {Touch} t2
   * @returns {number}
   */
  _getTouchDistance(t1, t2) {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
